
import React from "react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";

interface InterviewStageProps {
  candidate?: {
    id: number;
    name: string;
    avatar: string;
    role: string;
    description: string;
    skills: string[];
    benefits: string;
  };
  roleCapabilities: Record<string, string[]>;
  onSelectPlan: (plan: string) => void;
}

const InterviewStage: React.FC<InterviewStageProps> = ({ candidate, roleCapabilities, onSelectPlan }) => {
  if (!candidate) {
    return <div>Please select a candidate first.</div>;
  }

  const capabilities = roleCapabilities[candidate.role as keyof typeof roleCapabilities] || [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4">
        <h3 className="text-2xl font-bold">Meet {candidate.name}</h3>
        <p className="text-muted-foreground">
          Let's learn more about what your AI assistant can do for your business.
        </p>
        
        <div className="mt-6 space-y-6">
          {/* Candidate Overview */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-4">
                <div className="text-5xl">{candidate.avatar}</div>
                <div>
                  <CardTitle className="text-xl">{candidate.name}</CardTitle>
                  <CardDescription className="text-sm mt-1">
                    <div className="flex flex-wrap gap-1">
                      {candidate.skills.map((skill) => (
                        <Badge key={skill} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{candidate.description}</p>
            </CardContent>
          </Card>
          
          {/* Capabilities */}
          <div className="space-y-2">
            <h4 className="font-semibold text-lg">Key Capabilities</h4>
            <Card>
              <CardContent className="pt-6">
                <ul className="space-y-4">
                  {capabilities.map((capability, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Benefits */}
          <div className="space-y-2">
            <h4 className="font-semibold text-lg">Business Impact</h4>
            <Card className="bg-primary/5 border-none">
              <CardContent className="pt-6">
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">With {candidate.name}:</span> {candidate.benefits}
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-2 pt-4">
            <h4 className="font-semibold text-lg">Choose a Plan</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <Button
                variant="outline"
                size="lg"
                className="h-auto py-6 justify-start"
                onClick={() => onSelectPlan('basic')}
              >
                <div className="text-left">
                  <h5 className="font-bold text-lg">Basic Plan</h5>
                  <p className="text-muted-foreground text-sm">Essential features for small businesses</p>
                </div>
              </Button>
              
              <Button
                variant="default"
                size="lg"
                className="h-auto py-6 justify-start relative overflow-hidden"
                onClick={() => onSelectPlan('pro')}
              >
                <div className="absolute -right-10 -top-2 bg-yellow-500 text-black text-xs px-10 py-1 rotate-45">
                  POPULAR
                </div>
                <div className="text-left">
                  <h5 className="font-bold text-lg">Pro Plan</h5>
                  <p className="text-muted-foreground text-sm">Advanced features for growing businesses</p>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewStage;
